import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

import { VariableInputTag } from "./VariableInput.types";
import { VariableSuggestionList, VariableSuggestionListHandle } from "./VariableSuggestionList";

interface EditPillDropdownProps {
  anchorRect: DOMRect;
  variables: VariableInputTag[];
  hostElement?: HTMLElement | null;
  onSelect(tag: VariableInputTag): void;
  onClose(): void;
}

export function EditPillDropdown({ anchorRect, variables, hostElement, onSelect, onClose }: EditPillDropdownProps): React.ReactElement {
  const listRef = useRef<VariableSuggestionListHandle>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const target = hostElement ?? document.body;

  // When portaling into a transformed Radix Dialog, position:fixed becomes
  // relative to the dialog rather than the viewport — switch to position:absolute
  // and offset against the dialog's visual rect.
  const style = useMemo<React.CSSProperties>(() => {
    if (target === document.body) {
      return { position: "fixed", top: anchorRect.bottom + 4, left: anchorRect.left };
    }
    const hostRect = target.getBoundingClientRect();
    return {
      position: "absolute",
      top: anchorRect.bottom - hostRect.top + 4,
      left: anchorRect.left - hostRect.left
    };
  }, [target, anchorRect]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (listRef.current?.onKeyDown(e)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    // Close on outer scroll (so the dropdown doesn't detach from its anchor),
    // but ignore scrolls that happen INSIDE the dropdown's own scroll list.
    const handleScroll = (e: Event) => {
      const t = e.target as Node | null;
      if (t && portalRef.current?.contains(t)) return;
      onClose();
    };
    window.addEventListener("keydown", handleKey, true);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("keydown", handleKey, true);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={portalRef}
      className="VariableInput__suggestionPortal VariableInput__editPortal"
      style={style}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <VariableSuggestionList ref={listRef} items={variables} onSelect={onSelect} />
    </div>,
    target
  ) as React.ReactElement;
}
